import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  Column as _Column,
  Table as _Table,
  TableProps as _TableProps,
  AutoSizer as _AutoSizer,
  AutoSizerProps,
} from 'react-virtualized';
import type { ColumnProps } from 'react-virtualized/dist/es/Table';
import { KeyValueInTable, ArrowStringInTable } from './renderers';

type SearchBlockRenderer = (args: any) => React.ReactNode;

const searchBlocksInTable: Record<string, SearchBlockRenderer> = {
  keyValue: KeyValueInTable,
  arrowString: ArrowStringInTable,
};

const BaseTable = _Table as unknown as FC<_TableProps>;
const AutoSizer = _AutoSizer as unknown as FC<AutoSizerProps> & _AutoSizer;
const Column = _Column as unknown as React.ComponentClass<ColumnProps>;

const TableWrapper = styled.div<{ height: number }>`
  width: 100%;
  height: ${({ height }) => height}px;
  max-height: 350px;
  position: relative;

  .ReactVirtualized__Table__headerRow {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    font-size: 16px;
    font-weight: 500;
  }

  .ReactVirtualized__Table__row {
    display: flex;
    color: #4c5e72;
    padding-right: 5px;
    font-size: 15px;
    font-weight: 400;
  }

  .ReactVirtualized__Table__rowColumn:nth-of-type(1) {
    padding-right: 0px !important;
  }

  .ReactVirtualized__Table__headerColumn:nth-of-type(2),
  .ReactVirtualized__Table__rowColumn:nth-of-type(2) {
    padding-left: 12px !important;
  }

  .ReactVirtualized__Table__Grid {
    outline: 0 !important;
    box-shadow: none !important;
  }

  .ReactVirtualized__Table__headerColumn {
    width: 100%;
    height: 0px;
    > div {
      padding-bottom: 3px;
      width: 100%;
      height: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: flex-end;
    }
  }
`;

type SearchResultRow = Record<string, any> & {
  name: string;
};

export type SearchResultsTableProps = {
  data: SearchResultRow[];
  keysToType: Record<string, keyof typeof searchBlocksInTable | string>;
  name: string;
  onRowClick: (template: any) => void;
  searchValue: string;
};

const SearchResultsTable: React.FC<SearchResultsTableProps> = ({ data, keysToType, name, onRowClick, searchValue }) => {
  const itemsNm = data?.length ?? 0;
  const tableHeight = useMemo(() => {
    if (!itemsNm) return 0;
    const computed = itemsNm * 40;
    return computed > 350 ? 350 : computed;
  }, [itemsNm]);

  const [showImage, setShowImage] = useState(false);

  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [prevScrollOffset, setPrevScrollOffset] = useState(0);
  const [prevTimestamp, setPrevTimestamp] = useState(Date.now());

  const handleScroll = useCallback(
    ({ scrollTop }: { scrollTop: number }) => {
      const currentTimestamp = Date.now();
      const deltaScroll = Math.abs(scrollTop - prevScrollOffset);
      const deltaTime = currentTimestamp - prevTimestamp;
      let inSpeed = 0;

      if (deltaTime > 0) {
        const speed = deltaScroll / deltaTime;
        inSpeed = speed;
      }

      setPrevScrollOffset(scrollTop);
      setPrevTimestamp(currentTimestamp);

      if (scrollTimeoutRef.current) {
        if (inSpeed < 3) {
          setShowImage(true);
        } else {
          setShowImage(false);
        }
        clearTimeout(scrollTimeoutRef.current);
      }

      scrollTimeoutRef.current = setTimeout(() => {
        setShowImage(true);
      }, 50);
    },
    [prevScrollOffset, prevTimestamp],
  );

  useEffect(() => {
    setShowImage(itemsNm < 6);
  }, [itemsNm]);

  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  return (
    <TableWrapper height={tableHeight}>
      <AutoSizer>
        {({ width, height }: { width: number; height: number }) => {
          const blockTypeKey = keysToType[name];
          const SearchBlockComponent = searchBlocksInTable[blockTypeKey] ?? KeyValueInTable;

          return (
            <BaseTable
              width={width}
              height={height > 350 ? 350 : height}
              maxHeight={350}
              headerHeight={0}
              rowHeight={30}
              rowCount={data?.length ?? 0}
              rowGetter={({ index }) => data[index]}
              onScroll={handleScroll}
            >
              <Column
                label="Search Result"
                dataKey="name"
                width={width}
                cellRenderer={props => SearchBlockComponent({ ...props, searchValue, onRowClick, showImage })}
              />
            </BaseTable>
          );
        }}
      </AutoSizer>
    </TableWrapper>
  );
};

export default SearchResultsTable;
